<script lang="ts">
	import { t } from 'svelte-i18n';
	import { SVG } from '@wiseed/wikit';
	import { capitalize, formatAmountWithCurrency } from '@wiseed/wikit';
	import { OperationStatusEnum } from '@app/models/api/enums/OperationStatusEnum';
	import { getTargetTypeFromEN } from '@app/models/api/enums/TargetTypeEnum';
	import type { OperationModel } from '@app/models/api/OperationModel';
	import RateProject from '../../RateProject.svelte';
	import { isDateBefore } from '@wiseed/wikit';
	import CollectProgressIndicator from '../../CollectProgressIndicator.svelte';
	import ProjectAlertSms from './ProjectAlertSms.svelte';
	import { page } from '$app/stores';
	import ButtonVote from '@app/components/ButtonVote.svelte';
	import {
		getOperationModelAmountNeeded,
		hasAmountReachedExcludingWiseed,
		isCollectAlmostEnd,
		isCollectEnded,
		isCollectRunning
	} from '@app/utils/operations.js';
	import { OperationCategoryEnum } from '@app/models/api/enums/OperationCategoryEnum.js';
	import { TagSecondary } from '@wiseed/wikit';
	import { Hint } from '@wiseed/wikit';
	import type ModalComponent from '../elements/ProjectAlertSmsModal.svelte';

	export let operation: OperationModel;
	export let onVote: () => void;

	let modal: typeof ModalComponent | null;

	const loadModal = async () => {
		try {
			let { default: Modal } = await import('../elements/ProjectAlertSmsModal.svelte');
			if (Modal) {
				modal = Modal;
			}
		} catch (e) {
			console.error(e);
		}
	};

	const closeModal = () => {
		modal = null;
	};
</script>

<div class="flex flex-col space-y-4 md:space-y-5 pb-4 md:pb-0">
	<div class="space-y-2">
		{#if String(operation.targetType) !== String(operation.category)}
			<div>
				<TagSecondary
					props={{
						label: $t(`targetType.${capitalize(getTargetTypeFromEN(operation.targetType))}`),
						classNames: 'self-start text-xs'
					}}
				/>
			</div>
		{/if}
		{#if operation?.shortProjectDescription}
			<div class="text-2xl">
				{operation.shortProjectDescription}
			</div>
		{/if}

		{#if operation.percentBondYield}
			<div class="text-lg text-secondary">
				{$t('operation.headers.interestRate')}
				{operation.percentBondYield} %<sup>*</sup>
			</div>
		{/if}
	</div>
	{#if operation.status?.includes(OperationStatusEnum.FUNDING)}
		{#if operation.category !== OperationCategoryEnum.FUNDS && !operation.clotureEnCours && !isCollectEnded(operation)}
			<CollectProgressIndicator {operation} />
		{/if}
		<div class="grid footer-grid grid-cols-2 gap-3 text-secondary">
			<div class="flex flex-col">
				{#if !operation.isOPC && operation.nbOfInvestors && (isCollectRunning(operation) || isCollectAlmostEnd(operation))}
					<div class="font-semibold text-xl tracking-normal">{operation.nbOfInvestors}</div>
					<div>{$t('investors', { values: { n: operation.nbOfInvestors } })}</div>
				{/if}
			</div>
			<div class="flex flex-col">
				{#if operation.amountNeededWiseeders || operation.amountNeededPremium || operation.amountReserveInstitutional}
					<div class="font-semibold text-xl tracking-normal flex justify-end items-center gap-2">
						{formatAmountWithCurrency(getOperationModelAmountNeeded(operation))}
						{#if hasAmountReachedExcludingWiseed(operation)}
							<Hint
								tooltip={{
									content: $t('operation.amountHint', {
										values: {
											amountWiSEED: formatAmountWithCurrency(
												getOperationModelAmountNeeded(operation) -
													operation.amountReachedExcludingWiseed
											),
											amountOutsideWiSEED: formatAmountWithCurrency(
												operation.amountReachedExcludingWiseed
											)
										}
									})
								}}
							/>
						{/if}
					</div>
					<div>{$t('operation.amountToSeek')}</div>
				{/if}
			</div>
		</div>
	{/if}
	{#if operation.amountSubscribed}
		<div class="flex items-center text-primary space-x-2">
			<SVG name="check" size="small" />
			<span class="font-semibold text-sm"
				>{$t('operation.messages.youHaveAlreadyInvestedXEuros', {
					values: { amount: operation.amountSubscribed }
				})}</span
			>
		</div>
	{/if}

	{#if operation.status?.includes(OperationStatusEnum.FUNDING)}
		<div class="text-secondary">
			<ProjectAlertSms
				walletOk={$page.data?.user?.status?.walletOk}
				{operation}
				openModal={loadModal}
			/>
		</div>
	{:else if operation.status?.includes(OperationStatusEnum.VOTE)}
		<div
			class="flex flex-col md:flex-row items-center justify-around w-full space-y-4 md:space-y-0"
		>
			<div class="flex items-center">
				<SVG name="favorite" classNames="text-primary mr-2" />
				<span
					><span class="font-bold">{operation.nbFollowers}</span>
					{capitalize($t('operation.interested'))}</span
				>
			</div>
			{#if isDateBefore(new Date(), operation.voteEndDate) && !operation.isVoteOngoingAudit}
				{#if !operation.userVote}
					<ButtonVote
						openModal={() => {
							onVote();
						}}
					/>
				{:else}
					<div class="bg-white p-3 rounded-lg">
						<RateProject
							rating={operation.userVote
								? operation.userVote
								: (operation.statsVotes?.nbVotes ?? 0)}
							hasAlreadyVoted={!!operation.userVote}
						/>
					</div>
				{/if}
			{/if}
		</div>
	{/if}
</div>

<svelte:component this={modal} operationId={operation.id} on:closeModal={() => closeModal()} />

<style lang="postcss">
	.footer-grid {
		& > div:nth-child(2) {
			@apply text-right;
		}
		& > div:first-child {
			@apply text-left;
		}
	}
</style>
